import React, { useState, Dispatch, HTMLAttributes, ReactElement } from 'react';
import '../../base.scss';
import './Tabs.scss';
import { classNames } from '../../utilities';
import { useId } from '../../utilities/hooks/use-id';

type TabChild = ReactElement & { itemKey?: string };

export interface TabsProps extends HTMLAttributes<HTMLDivElement> {
  /** The tabs to display */
  children: TabChild | TabChild[];
  /** Attach a custom class to the tab container */
  className?: string;
  /** stable key for tests or 3rd party handlers */
  'data-testid'?: string;
  /** Initially visible tab for uncontrolled tabs */
  defaultSelectedTabKey?: string;
  /** Override generated id */
  id?: string;
  /** Callback when a tab header is clicked  */
  onTabClick?: (key: string, event?: React.MouseEvent<HTMLElement>) => void;
  /** Render the content for hidden tabs.  Useful for SEO or uncontrolled stateful content. */
  renderAll?: boolean;
  /** Selected and visible tab for controlled tabs */
  selectedTabKey?: string;
  /**
   * Support multiple tab styles
   * @default default
   */
  variant?: 'default' | 'stretch';
}

/*
 The Tabs component is both the tablist headers and tabpanels that render children content.
 */
export function Tabs(props: TabsProps) {
  const {
    children,
    className,
    defaultSelectedTabKey,
    id,
    renderAll = false,
    selectedTabKey,
    variant = 'default',
  } = props;

  if (defaultSelectedTabKey && selectedTabKey) {
    throw new Error('Tabs should either be controlled (selectedTabKey) or uncontrolled (defaultSelectedTabKey).');
  }

  if (!children) {
    throw new Error('Tabs must have at least one tab as a child.');
  }

  const generatedId = useId(id, 'ps-tabs');
  const tabsId = id ? id : generatedId;

  const firstTabKey = Array.isArray(children) ? children[0].itemKey || '0' : '0';
  const [uncontrolledSelectedTab, setUncontrolledSelectedTab] = useState(defaultSelectedTabKey || firstTabKey);

  /* logic to find which tab is active */
  let activeTabKey;
  let controlled = true;
  if (selectedTabKey) {
    activeTabKey = selectedTabKey;
  } else {
    controlled = false;
    activeTabKey = uncontrolledSelectedTab;
  }

  const tabsClassNames = classNames('ps-tabs', variant === 'stretch' ? 'ps-tabs--stretch' : '', className);

  return (
    <div className={tabsClassNames} id={tabsId}>
      <div className="ps-tabs__tab-list" role="tablist">
        {getTabs(props, activeTabKey, tabsId, controlled, setUncontrolledSelectedTab)}
      </div>
      {getTabPanels(props, activeTabKey, renderAll, tabsId)}
    </div>
  );
}

/*
  Tabs are the actual tablist entries
 */
const getTabs = (
  props: TabsProps,
  activeTabKey: string,
  tabsId: string,
  controlled: boolean,
  setUncontrolledSelectedTab: Dispatch<string>
) => {
  const { children } = props;

  const tabs = React.Children.map(children, (child, index) => {
    if (!child) return;

    const id = generateTabId(child.itemKey, tabsId, index);
    const key = child.itemKey || String(index);

    const onTabClick = props.onTabClick;

    /*
      Clone each child <Tab> into the tab header so we override props.
     */
    return React.cloneElement(child, {
      active: key === activeTabKey,
      children: child.props.title,
      id: id, // for aria-labelledby
      itemKey: key,
      onTabClick: (clickedTabKey: string, event: React.MouseEvent<HTMLElement>) => {
        if (!controlled) {
          setUncontrolledSelectedTab(clickedTabKey);
        }

        // bubble up if <Tabs> has a passed in external handler
        if (onTabClick) {
          onTabClick(clickedTabKey, event);
        }
      },
    });
  });

  return tabs;
};

/*
  TabPanels are the contents that show up "within" the visible / selected tab.
 */
const getTabPanels = (props: TabsProps, activeTabKey: string, renderAll: boolean, tabsId: string) => {
  const { children } = props;

  /*
   Each <Tab> represents both the tab in the tablist
     but also the tabpanel with child content.
   */
  const tabsPanels = React.Children.map(children, (child, index) => {
    if (!child) return;

    const id = generateTabId(child.itemKey, tabsId, index);
    const key = child.itemKey || String(index);

    const isPanelActive = key === activeTabKey;
    const props = {
      key,
      'aria-hidden': !isPanelActive,
      'aria-labelledby': id,
      role: 'tabpanel',
    };

    if (!isPanelActive && !renderAll) return;

    const tabPanelClasses = classNames('ps-tabs__tab-panel', isPanelActive ? 'ps-tabs__tab-panel--active' : '');

    return (
      // note: don't pass down title, or you'll see a tooltip anywhere you hover in the div
      <div {...child.props} {...props} title="" className={tabPanelClasses}>
        {child.props.children}
      </div>
    );
  });

  return tabsPanels;
};

const generateTabId = (itemKey: string | undefined, tabsId: string, index: number) => {
  return itemKey || `${tabsId}-Tab${index}`;
};
